import { Instagram, Twitter, Linkedin } from 'lucide-react';
import { LANDING } from 'constants/testIds';
import useTheme from 'hooks/useTheme';
import { useLegal } from 'components/app/LegalModal';

const NAV_LINKS = [
  { label: 'About', key: 'about' },
  { label: 'Contact us', key: 'contact' },
];

const LEGAL_LINKS = [
  { label: 'Terms & Conditions', key: 'terms' },
  { label: 'Community guidelines', key: 'community' },
];

export const Footer = () => {
  const { theme } = useTheme();
  const legal = useLegal();
  const logoSrc = theme === 'light' ? '/qreativs-logo.png' : '/qreativs-logo-dark.png';
  return (
    <footer
      data-testid={LANDING.footer}
      className="relative border-t border-zinc-900 bg-zinc-950"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20 md:py-24">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-12">
          {/* Wordmark + tagline */}
          <div className="max-w-md">
            <a
              href="#top"
              data-testid={LANDING.footerWordmark}
              className="inline-flex items-center"
              aria-label="Qreativs"
            >
              <img
                src={logoSrc}
                alt="Qreativs"
                className="h-10 md:h-12 w-auto select-none"
                draggable={false}
              />
            </a>

            <div className="mt-8 flex items-center gap-3">
              {[
                { Icon: Instagram, label: 'Instagram', href: 'https://instagram.com/qreativs' },
                { Icon: Twitter, label: 'Twitter', href: 'https://twitter.com/qreativs' },
                { Icon: Linkedin, label: 'LinkedIn', href: 'https://linkedin.com/company/qreativs' },
              ].map(({ Icon, label, href }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={`landing-footer-social-${label.toLowerCase()}`}
                  className="landing-btn landing-btn-icon"
                >
                  <Icon size={16} />
                </a>
              ))}
            </div>
          </div>

          {/* Link groups */}
          <div className="flex flex-col sm:flex-row gap-12 sm:gap-16 md:gap-20">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-medium mb-5">
                Company
              </div>
              <ul className="space-y-3">
                {NAV_LINKS.map((l) => (
                  <li key={l.label}>
                    <button
                      type="button"
                      onClick={() => legal?.openPage(l.key)}
                      data-testid={`landing-footer-link-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
                      className="text-sm text-zinc-300 hover:text-zinc-50 transition-colors text-left"
                    >
                      {l.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-medium mb-5">
                Legal
              </div>
              <ul className="space-y-3">
                {LEGAL_LINKS.map((l) => (
                  <li key={l.label}>
                    <button
                      type="button"
                      onClick={() => legal?.openPage(l.key)}
                      data-testid={`landing-footer-link-${l.label.toLowerCase().replace(/[\s&]+/g, '-').replace(/-+/g, '-')}`}
                      className="text-sm text-zinc-300 hover:text-zinc-50 transition-colors text-left"
                    >
                      {l.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-zinc-900 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="text-xs text-zinc-500">
            © {new Date().getFullYear()} Qreativs.
          </div>
          <a
            href="mailto:support@qreativs.com"
            data-testid="landing-footer-support-email"
            className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
          >
            support@qreativs.com
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
